import { listDuelSpecKeys } from './duelSpecs';
import { beatsOn, isPlayableOn } from './overtakeCards';
import { HAND_SIZE, cardOf, dealMatch } from './overtakeDeck';

/**
 * A match of the Overtake, as a pure reducer (SPEC-011 PR-11.1).
 *
 * Every legal move is a function from a match to a match, and an illegal one
 * answers the match it was given rather than throwing. That is the same posture
 * `duelRun` takes and it is load-bearing for the same reason: the screen renders
 * from this state, so a refused move has to leave something renderable behind.
 * A second tap on a card that is already down, an opponent's timer landing in
 * the frame the reader passed — both are reachable by construction, and neither
 * may advance a round twice.
 *
 * Six statuses:
 *
 *   leading      the leader must put down a card AND name a figure.
 *   renaming     a veto refused the figure. The card is still on the table and
 *                the leader must name a different one (SPEC-011 D-8).
 *   answering    a figure is in play and the other side must beat it or pass.
 *   roundOver    somebody passed. The pot has been awarded and is on screen; the
 *                hands have NOT been refilled yet.
 *   over         a hand ran out. Nothing moves again.
 *   unavailable  this collection cannot hold a match. The catalogue is fetched
 *                and the collection grows, so this is a designed state.
 *
 * The refill is deliberately in `advanceRound` rather than in `pass`, so a
 * reader gets to look at the pot they just won or lost before five new cards
 * arrive underneath it. It is the same seam the duel puts between `answerRun`
 * and `advanceRun`, for the same reason.
 */

/** Who names the figure next round. All three ship; the screen offers them. */
export const LEAD_RULES = ['winner', 'alternate', 'loser'];

/** A fresh set of one-shot powers, for one side (SPEC-011 D-7, D-8, D-10). */
function freshPowers() {
    return { counter: true, veto: true, double: true };
}

function unavailableMatch(leadRule) {
    return {
        status: 'unavailable',
        hands: { player: [], opponent: [] },
        stock: [],
        pots: { player: [], opponent: [] },
        bonuses: { player: 0, opponent: 0 },
        pile: [],
        spec: null,
        direction: null,
        doubled: false,
        spent: [],
        lead: 'player',
        turn: 'player',
        powers: { player: freshPowers(), opponent: freshPowers() },
        leadRule,
        round: 0,
        lastRound: null,
    };
}

/**
 * Deal a match from these card ids.
 *
 * `lead` is a parameter rather than a coin toss inside here, because who opens
 * is the one thing about a match that a caller genuinely decides: a local game
 * opens on the reader, and PR-11.4's invited match opens on whoever created it.
 * Defaulting it to `player` and letting a caller pass otherwise is a smaller
 * seam than a second `random` draw nobody could pin.
 */
export function startMatch(ids, {
    random = Math.random,
    handSize = HAND_SIZE,
    leadRule = 'winner',
    lead = 'player',
} = {}) {
    const rule = LEAD_RULES.includes(leadRule) ? leadRule : 'winner';
    const dealt = dealMatch(ids, { random, handSize });

    if (!dealt) {
        return unavailableMatch(rule);
    }

    return {
        ...unavailableMatch(rule),
        status: 'leading',
        hands: dealt.hands,
        stock: dealt.stock,
        lead,
        turn: lead,
        round: 1,
    };
}

/* -------------------------------------------------------------------------
   Selectors. The screen asks these rather than deciding legality itself
   (SPEC-011 D-15) — a hand that greys the cards which cannot be played is
   kinder than one that lets a reader find out by tapping.
   ---------------------------------------------------------------------- */

/** The figures nobody has called yet, in the table's own order. */
export function availableSpecs(match) {
    const spent = new Set(match?.spent ?? []);

    return listDuelSpecKeys().filter((spec) => !spent.has(spec));
}

/** The card currently at the top of the pile, or null before anything is down. */
export function standingCard(match, deck) {
    const top = match?.pile?.[match.pile.length - 1];

    return top ? cardOf(deck, top.id) : null;
}

/** Which of the leader's cards could open a round on this figure. */
export function leadableCards(match, deck, spec) {
    if (!match || !availableSpecs(match).includes(spec)) {
        return [];
    }

    return (match.hands[match.lead] ?? [])
        .filter((id) => isPlayableOn(cardOf(deck, id), spec));
}

/**
 * Which cards the side to act could put down right now.
 *
 * Empty in every status but `answering`, which is not a guard: the hand is drawn
 * from this list in every status, and a leading hand that showed every card as
 * playable before a figure had been named would be telling the reader something
 * that is not true yet.
 */
export function legalPlays(match, deck) {
    if (match?.status !== 'answering') {
        return [];
    }

    const standing = standingCard(match, deck);

    return (match.hands[match.turn] ?? [])
        .filter((id) => beatsOn(cardOf(deck, id), standing, match.spec, match.direction));
}

/** Can the side to act still invert the figure? Once per match, and not by the leader. */
export function canCounterCall(match) {
    return match?.status === 'answering'
        && match.turn !== match.lead
        && Boolean(match.powers?.[match.turn]?.counter);
}

/** Can the side to act still refuse the figure? Only before anybody has answered it. */
export function canVeto(match) {
    return match?.status === 'answering'
        && match.turn !== match.lead
        && match.pile?.length === 1
        && Boolean(match.powers?.[match.turn]?.veto);
}

/** Can the leader still double this round? Only as they open it. */
export function canDouble(match) {
    return match?.status === 'leading' && Boolean(match.powers?.[match.lead]?.double);
}


/**
 * Every card the leader could open SOME round with.
 *
 * `leadableCards` answers for one named figure; this answers for the hand as a
 * whole, which is what a screen needs before a figure has been chosen. The
 * reader picks a card first and names the figure second (the lead is one move,
 * but it is collected in two taps), so the hand has to know which cards lead
 * anything at all before it knows what they will lead.
 *
 * In practice it excludes almost nothing — every car carries a top speed — and
 * that is fine. It exists for the hand that is genuinely stuck: a Wankel and
 * four cars on a board where only cylinders and production are left.
 */
export function leadableHand(match, deck) {
    if (match?.status !== 'leading') {
        return [];
    }

    const specs = availableSpecs(match);

    return (match.hands[match.lead] ?? [])
        .filter((id) => specs.some((spec) => isPlayableOn(cardOf(deck, id), spec)));
}
